import { ChangeEvent, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { MESSAGES } from '@app/constant';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCreateImageMutation, useDeleteImageMutation } from '@modules/images';
import {
  useCreateIngredientMutation,
  useUpdateIngredientMutation,
  useLazyGetIngredientByIdQuery
} from '@modules/ingredient';
import { useRenderFileViewHook } from '@app/hooks/imagesRender';

import { IFlowProcess, IIngredientFormData, OnChangeFunctionProps } from './helper/types';
import { createMarkSchema, initialFlowState, initialFormState } from './helper/data';
import { onChangeUpload, handleDeleteImage } from './helper/localHelper';
import { prepareClonedData, prepareImageUploadPromises } from './helper/utils';
import Ingredients from '@pages/Ingredients';

export const useIngredientsLogic = () => {
  const { id } = useParams()

  const [createIngredient] = useCreateIngredientMutation()
  const [updateIngredient] = useUpdateIngredientMutation()
  const [createImage] = useCreateImageMutation()
  const [deleteImage] = useDeleteImageMutation()
  const [getIngredientsById] = useLazyGetIngredientByIdQuery();

  const [flowProcess, setFlowProcess] = useState<IFlowProcess>(initialFlowState)

  const methods = useForm<IIngredientFormData>({
    // @ts-ignore
    resolver: yupResolver(createMarkSchema),
    mode: 'onSubmit',
    defaultValues: initialFormState
  });

  const {
    reset,
    watch,
    register,
    setValue,
    setError,
    handleSubmit,
    formState: { errors }
  } = methods;

  const recipe = watch('recipe')
  const images = watch('uploadedFileList')
  const existingImagesWatch = watch('existingImages')

  const previews = useRenderFileViewHook(images, existingImagesWatch)

  const handleLoadingState = (isLoading: boolean, successMessage: string = '') => {
    setFlowProcess((prev) => ({
      ...prev,
      isButtonLoading: isLoading,
      isSuccess: successMessage,
      largeImageMessage: '',
    }));
  };

  const onSubmit = async (data: IIngredientFormData) => {
    handleLoadingState(true);

    let imagePaths: string[] = [];

    try {
      const imageUploadPromises = prepareImageUploadPromises(data.uploadedFileList, createImage);

      const uploadedImages = await Promise.all(imageUploadPromises || []);

      imagePaths = uploadedImages?.map((result) => result?.id || '');

      const clonedData = prepareClonedData(data, imagePaths);

      if (id) {
        await updateIngredient({ id, body: clonedData }).unwrap()
        getIngredientData()
      } else {
        await createIngredient(clonedData).unwrap();
        reset(initialFormState);
      }

      handleLoadingState(false, id
        ? MESSAGES.ingredientsHasBeenSuccessfullyUpdated
        : MESSAGES.ingredientsHasBeenSuccessfullyCreated);
    } catch (error: any) {
      if (imagePaths?.length) {
        // If after first step, when FileList uploaded in backend.
        // And then before ingredient creation appears some problems.
        // In that case we delete all uploaded images

        imagePaths.forEach(async (imageId) => {
          await deleteImage({ id: imageId })
        })
      }

      handleLoadingState(false, error?.data?.message || MESSAGES.somethingWentWrong);
    }
  };

  const getIngredientData = async () => {
    if (!id) {
      setFlowProcess((prev) => ({ ...prev, isLoading: false }))

      return
    }

    try {
      const gotIngredient = await getIngredientsById(id).unwrap()

      reset({
        ...gotIngredient,
        existingImages: gotIngredient.images,
      });
    } catch (error) {
      console.log("Error", error);
    } finally {
      setFlowProcess((prev) => ({ ...prev, isLoading: false }))
    }
  }

  useEffect(() => {
    getIngredientData()
  }, [id])

  return {
    ...flowProcess,
    paramId: id,
    methods,
    errors,
    watch,
    setValue,
    setError,
    setFlowProcess,
    onSubmit,
    register,
    handleSubmit,
    handleDeleteImage: (bas64: string) => handleDeleteImage({
      bas64,
      previews,
      images,
      watch,
      setValue,
      setState: setFlowProcess,
    }),
    onChangeUpload: (props: OnChangeFunctionProps) => onChangeUpload({
      watch,
      setValue,
      setState: setFlowProcess,
      setError,
      ...props,
    }),
  }
}
